import React from 'react';
import { Chart, Radar, Bubble } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  RadialLinearScale,
  ArcElement,
  BubbleController,
  RadarController,
  LineController,
  BarController,
  Filler
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  RadialLinearScale,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  BubbleController,
  RadarController,
  LineController,
  BarController,
  Filler
);

export interface MetricData {
  metricName: string;
  userValue: number;
  average: number;
  percentile25: number;
  percentile75: number;
  percentileRank: number;
  desiredTrend: 'up' | 'down';
  unit: string;
  isMonetary: boolean;
}

interface PerformanceMetricsChartProps {
  metrics: MetricData[];
  chartType?: 'radar' | 'bubble' | 'bar';
  height?: number;
}

// Utility function to format values for display
const formatValue = (value: number, unit: string, isMonetary: boolean): string => {
  if (isMonetary) {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M ${unit.replace('$', '')}`;
    } else if (value >= 1000) {
      return `$${(value / 1000).toFixed(1)}k ${unit.replace('$', '')}`;
    }
    return `$${value.toFixed(2)} ${unit.replace('$', '')}`;
  }
  
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M ${unit}`;
  } else if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}k ${unit}`;
  }
  
  return `${value.toFixed(2)} ${unit}`;
};

// Component for rendering different types of performance charts
const PerformanceMetricsChart: React.FC<PerformanceMetricsChartProps> = ({ 
  metrics, 
  chartType = 'radar',
  height = 400
}) => {
  // For metrics where lower is better (like expenses), invert the percentile rank for visualization
  const getPercentileForDisplay = (metric: MetricData) => {
    return metric.desiredTrend === 'down' 
      ? 100 - metric.percentileRank 
      : metric.percentileRank;
  };

  // Common options for all chart types
  const commonOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      tooltip: {
        callbacks: {
          label: function(context: any) {
            const metric = metrics[context.dataIndex];
            if (!metric) return '';
            
            const label = metric.metricName;
            const value = formatValue(metric.userValue, metric.unit, metric.isMonetary);
            const avg = formatValue(metric.average, metric.unit, metric.isMonetary);
            
            return [
              `${label}:`,
              `Your value: ${value}`,
              `Industry avg: ${avg}`,
              `Percentile: ${metric.percentileRank}%`
            ];
          }
        }
      }
    }
  };

  // Chart configuration based on chart type
  switch(chartType) {
    case 'radar':
      return (
        <div style={{ height: `${height}px` }}>
          <Radar 
            data={{
              labels: metrics.map(m => m.metricName),
              datasets: [
                {
                  label: 'Your Performance',
                  data: metrics.map(getPercentileForDisplay),
                  backgroundColor: 'rgba(53, 162, 235, 0.5)',
                  borderColor: 'rgba(53, 162, 235, 1)',
                  borderWidth: 2,
                },
                {
                  label: 'Industry Average',
                  data: Array(metrics.length).fill(50),
                  backgroundColor: 'rgba(255, 99, 132, 0.2)',
                  borderColor: 'rgba(255, 99, 132, 1)',
                  borderWidth: 2,
                  borderDash: [5, 5]
                }
              ],
            }}
            options={{
              ...commonOptions,
              scales: {
                r: {
                  min: 0,
                  max: 100,
                  ticks: {
                    display: false,
                  },
                  pointLabels: {
                    font: {
                      size: 12
                    }
                  }
                }
              }
            }}
          />
        </div>
      );

    case 'bubble':
      return (
        <div style={{ height: `${height}px` }}>
          <Bubble
            data={{
              datasets: metrics.map((metric, i) => {
                // Different colors for different metrics
                const colors = [
                  'rgba(53, 162, 235, 0.7)',
                  'rgba(255, 99, 132, 0.7)',
                  'rgba(75, 192, 192, 0.7)',
                  'rgba(255, 159, 64, 0.7)',
                  'rgba(153, 102, 255, 0.7)',
                  'rgba(255, 205, 86, 0.7)',
                ];
                
                const color = colors[i % colors.length];
                const borderColor = color.replace('0.7', '1');
                
                // Calculate bubble size based on percentile rank
                const bubbleSize = Math.max(15, metric.percentileRank / 2);
                
                return {
                  label: metric.metricName,
                  data: [{
                    x: i,
                    y: metric.userValue / metric.average, // Ratio to average
                    r: bubbleSize
                  }],
                  backgroundColor: color,
                  borderColor: borderColor,
                  borderWidth: 2
                };
              })
            }}
            options={{
              ...commonOptions,
              scales: {
                x: {
                  ticks: {
                    callback: function(value: any) {
                      const index = Number(value);
                      return metrics[index]?.metricName || '';
                    }
                  }
                },
                y: {
                  title: {
                    display: true,
                    text: 'Compared to Industry Average (1.0 = average)'
                  },
                  ticks: {
                    callback: function(value: any) {
                      return `${value}x`;
                    }
                  }
                }
              }
            }}
          />
        </div>
      );

    case 'bar':
    default:
      // Create normalized data showing performance relative to industry average
      const normalizedData = metrics.map(metric => ({
        ...metric,
        normalizedUserValue: metric.average > 0 ? (metric.userValue / metric.average) : 0,
        normalizedP25: metric.average > 0 ? (metric.percentile25 / metric.average) : 0,
        normalizedP75: metric.average > 0 ? (metric.percentile75 / metric.average) : 0
      }));

      return (
        <div style={{ height: `${height}px` }}>
          <Chart
            type="bar"
            data={{
              labels: metrics.map(m => m.metricName.replace(/\s+/g, '\n')), // Break long labels
              datasets: [
                {
                  label: 'Your Performance',
                  data: normalizedData.map(m => m.normalizedUserValue),
                  backgroundColor: normalizedData.map(m => {
                    const ratio = m.normalizedUserValue;
                    if (m.desiredTrend === 'up') {
                      return ratio >= 1.1 ? 'rgba(34, 197, 94, 0.7)' : // Green for good
                             ratio >= 0.9 ? 'rgba(251, 191, 36, 0.7)' : // Yellow for average
                             'rgba(239, 68, 68, 0.7)'; // Red for poor
                    } else {
                      return ratio <= 0.9 ? 'rgba(34, 197, 94, 0.7)' : // Green for good (lower is better)
                             ratio <= 1.1 ? 'rgba(251, 191, 36, 0.7)' : // Yellow for average
                             'rgba(239, 68, 68, 0.7)'; // Red for poor
                    }
                  }),
                  borderColor: normalizedData.map(m => {
                    const ratio = m.normalizedUserValue;
                    if (m.desiredTrend === 'up') {
                      return ratio >= 1.1 ? 'rgba(34, 197, 94, 1)' :
                             ratio >= 0.9 ? 'rgba(251, 191, 36, 1)' :
                             'rgba(239, 68, 68, 1)';
                    } else {
                      return ratio <= 0.9 ? 'rgba(34, 197, 94, 1)' :
                             ratio <= 1.1 ? 'rgba(251, 191, 36, 1)' :
                             'rgba(239, 68, 68, 1)';
                    }
                  }),
                  borderWidth: 2,
                  type: 'bar'
                },
                {
                  label: '25th Percentile',
                  data: normalizedData.map(m => m.normalizedP25),
                  backgroundColor: 'rgba(156, 163, 175, 0.3)',
                  borderColor: 'rgba(156, 163, 175, 0.8)',
                  borderWidth: 1,
                  type: 'line',
                  pointRadius: 0,
                  pointHoverRadius: 0,
                  borderDash: [5, 5],
                  fill: false
                },
                {
                  label: '75th Percentile',
                  data: normalizedData.map(m => m.normalizedP75),
                  backgroundColor: 'rgba(75, 85, 99, 0.3)',
                  borderColor: 'rgba(75, 85, 99, 0.8)',
                  borderWidth: 1,
                  type: 'line',
                  pointRadius: 0,
                  pointHoverRadius: 0,
                  borderDash: [5, 5],
                  fill: false
                }
              ]
            }}
            options={{
              ...commonOptions,
              interaction: {
                intersect: false,
                mode: 'index' as const
              },
              plugins: {
                ...commonOptions.plugins,
                tooltip: {
                  callbacks: {
                    title: function(context: any) {
                      const index = context[0].dataIndex;
                      return metrics[index]?.metricName || '';
                    },
                    label: function(context: any) {
                      const index = context.dataIndex;
                      const metric = metrics[index];
                      if (!metric) return '';
                      
                      if (context.datasetIndex === 0) {
                        const actualValue = formatValue(metric.userValue, metric.unit, metric.isMonetary);
                        const avgValue = formatValue(metric.average, metric.unit, metric.isMonetary);
                        const ratio = (metric.userValue / metric.average).toFixed(2);
                        return [
                          `Your Value: ${actualValue}`,
                          `Industry Avg: ${avgValue}`,
                          `Ratio: ${ratio}x average`
                        ];
                      } else if (context.datasetIndex === 1) {
                        return `25th Percentile: ${formatValue(metric.percentile25, metric.unit, metric.isMonetary)}`;
                      } else {
                        return `75th Percentile: ${formatValue(metric.percentile75, metric.unit, metric.isMonetary)}`;
                      }
                    }
                  }
                }
              },
              scales: {
                x: {
                  ticks: {
                    maxRotation: 45,
                    minRotation: 0,
                    font: {
                      size: 11
                    }
                  }
                },
                y: {
                  title: {
                    display: true,
                    text: 'Performance vs Industry Average (1.0 = average)',
                    font: {
                      size: 12,
                      weight: 'bold'
                    }
                  },
                  min: 0,
                  max: Math.max(2.5, Math.max(...normalizedData.map(m => Math.max(m.normalizedUserValue, m.normalizedP75))) * 1.1),
                  ticks: {
                    callback: function(value: any) {
                      return `${Number(value).toFixed(1)}x`;
                    },
                    font: {
                      size: 11
                    }
                  },
                  grid: {
                    color: function(context: any) {
                      return context.tick.value === 1 ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.1)';
                    },
                    lineWidth: function(context: any) {
                      return context.tick.value === 1 ? 2 : 1;
                    }
                  }
                }
              }
            }}
          />
          
          {/* Legend explaining the colors */}
          <div className="mt-4 flex flex-wrap gap-4 text-xs text-gray-600 justify-center">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-green-500 rounded"></div>
              <span>Above Average Performance</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-yellow-500 rounded"></div>
              <span>Average Performance</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-red-500 rounded"></div>
              <span>Below Average Performance</span>
            </div>
          </div>
        </div>
      );
  }
};

export default PerformanceMetricsChart;
